import React, {useEffect, useState} from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import {Add} from "@mui/icons-material";
import {graphQLServices} from "../../../services/GraphQLServices";
import {
	GET_ADMIN_EXTRACTOR_SETS,
	GET_ADMIN_ON_CHANGE_HANDLER_SETS,
	GET_ADMIN_EXPORTER_SETS,
	GET_ADMIN_SETTINGS,
	GET_ADMIN_VALIDATOR_SETS,
} from "../components/graphql/queries/gqlAdminSettings";
import {SAVE_ADMIN_SETTINGS} from "../components/graphql/mutations/gqlAdminMutations";
import {AdminSettings, ExporterSet, ExtractorSet, OnChangeHandlerSet, ValidatorSet} from "./adminTypes";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";

function AdminSettingsPageContent() {
	const [settings, setSettings] = useState<AdminSettings | null>(null);
	const [extractorSets, setExtractorSets] = useState<ExtractorSet[]>([]);
	const [validatorSets, setValidatorSets] = useState<ValidatorSet[]>([]);
	const [exporterSets, setExporterSets] = useState<ExporterSet[]>([]);
	const [onChangeHandlerSets, setOnChangeHandlerSets] = useState<OnChangeHandlerSet[]>([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [newMailAddress, setNewMailAddress] = useState("");

	useEffect(() => {
		const loadAll = async () => {
			setLoading(true);
			try {
				const [settingsResult, extractors, validators, exporters, handlers] = await Promise.all([
					graphQLServices.documentsClient.query({query: GET_ADMIN_SETTINGS, fetchPolicy: "no-cache"}),
					graphQLServices.documentsClient.query({query: GET_ADMIN_EXTRACTOR_SETS, fetchPolicy: "no-cache"}),
					graphQLServices.documentsClient.query({query: GET_ADMIN_VALIDATOR_SETS, fetchPolicy: "no-cache"}),
					graphQLServices.documentsClient.query({query: GET_ADMIN_EXPORTER_SETS, fetchPolicy: "no-cache"}),
					graphQLServices.documentsClient.query({query: GET_ADMIN_ON_CHANGE_HANDLER_SETS, fetchPolicy: "no-cache"}),
				]);
				setSettings(settingsResult.data.adminSettings);
				setExtractorSets(extractors.data.adminExtractorSets ?? []);
				setValidatorSets(validators.data.adminValidatorSets ?? []);
				setExporterSets(exporters.data.adminExporterSets ?? []);
				setOnChangeHandlerSets(handlers.data.adminOnChangeHandlerSets ?? []);
			} catch (e: any) {
				setError(e.message);
			} finally {
				setLoading(false);
			}
		};
		loadAll();
	}, []);

	const handleSave = async () => {
		if (!settings) return;
		setSaving(true);
		setError(null);
		try {
			await graphQLServices.documentsClient.mutate({
				mutation: SAVE_ADMIN_SETTINGS,
				variables: {
					input: {
						defaultExtractorSetId: settings.defaultExtractorSetId,
						defaultValidatorSetId: settings.defaultValidatorSetId,
						defaultOnChangeHandlerSetId: settings.defaultOnChangeHandlerSetId,
						defaultExporterSetId: settings.defaultExporterSetId,
						mailExporterSetId: settings.mailExporterSetId,
						mailFromAddresses: settings.mailFromAddresses.map(x => x.mailAddress),
					},
				},
			});
		} catch (e: any) {
			setError(e.message);
		} finally {
			setSaving(false);
		}
	};

	const addMailAddress = () => {
		if (!settings || !newMailAddress.trim()) return;
		setSettings({
			...settings,
			mailFromAddresses: [...settings.mailFromAddresses, {id: 0, mailAddress: newMailAddress.trim()}],
		});
		setNewMailAddress("");
	};

	const removeMailAddress = (address: string) => {
		if (!settings) return;
		setSettings({
			...settings,
			mailFromAddresses: settings.mailFromAddresses.filter(x => x.mailAddress !== address),
		});
	};

	if (loading) return <CircularProgress />;
	if (!settings) return <Alert severity="error">Failed to load settings</Alert>;

	return (
		<Box sx={{p: 3}}>
			<Typography variant="h5" gutterBottom>Document Settings</Typography>
			{error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
			<Paper sx={{p: 3}}>
				<Stack spacing={3}>
					<FormControl fullWidth>
						<InputLabel>Default Extractor Set</InputLabel>
						<Select
							label="Default Extractor Set"
							value={settings.defaultExtractorSetId ?? ""}
							onChange={e => setSettings({...settings, defaultExtractorSetId: e.target.value as number || null})}
						>
							<MenuItem value=""><em>None</em></MenuItem>
							{extractorSets.map(s => (
								<MenuItem key={s.id} value={s.id}>{s.code} — {s.description}</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl fullWidth>
						<InputLabel>Default Validator Set</InputLabel>
						<Select
							label="Default Validator Set"
							value={settings.defaultValidatorSetId ?? ""}
							onChange={e => setSettings({...settings, defaultValidatorSetId: e.target.value as number || null})}
						>
							<MenuItem value=""><em>None</em></MenuItem>
							{validatorSets.map(s => (
								<MenuItem key={s.id} value={s.id}>{s.code} — {s.description}</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl fullWidth>
						<InputLabel>Default Exporter Set</InputLabel>
						<Select
							label="Default Exporter Set"
							value={settings.defaultExporterSetId ?? ""}
							onChange={e => setSettings({...settings, defaultExporterSetId: e.target.value as number || null})}
						>
							<MenuItem value=""><em>None</em></MenuItem>
							{exporterSets.map(s => (
								<MenuItem key={s.id} value={s.id}>{s.code} — {s.description}</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl fullWidth>
						<InputLabel>Default OnChange Handler Set</InputLabel>
						<Select
							label="Default OnChange Handler Set"
							value={settings.defaultOnChangeHandlerSetId ?? ""}
							onChange={e => setSettings({...settings, defaultOnChangeHandlerSetId: e.target.value as number || null})}
						>
							<MenuItem value=""><em>None</em></MenuItem>
							{onChangeHandlerSets.map(s => (
								<MenuItem key={s.id} value={s.id}>{s.code} — {s.description}</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl fullWidth>
						<InputLabel>Mail Exporter Set</InputLabel>
						<Select
							label="Mail Exporter Set"
							value={settings.mailExporterSetId ?? ""}
							onChange={e => setSettings({...settings, mailExporterSetId: e.target.value as number || null})}
						>
							<MenuItem value=""><em>None</em></MenuItem>
							{exporterSets.map(s => (
								<MenuItem key={s.id} value={s.id}>{s.code} — {s.description}</MenuItem>
							))}
						</Select>
					</FormControl>

					<Box>
						<Typography variant="subtitle1" gutterBottom>Mail From Addresses</Typography>
						<Box sx={{display: "flex", flexWrap: "wrap", gap: 1, mb: 1}}>
							{settings.mailFromAddresses.map(addr => (
								<Chip
									key={addr.mailAddress}
									label={addr.mailAddress}
									onDelete={() => removeMailAddress(addr.mailAddress)}
								/>
							))}
						</Box>
						<Box sx={{display: "flex", gap: 1, alignItems: "center"}}>
							<TextField
								size="small"
								label="Add email address"
								value={newMailAddress}
								onChange={e => setNewMailAddress(e.target.value)}
								onKeyDown={e => {
									if (e.key === "Enter") addMailAddress(); 
								}}
							/>
							<IconButton onClick={addMailAddress} color="primary">
								<Add />
							</IconButton>
						</Box>
					</Box>

					<Box>
						<Button variant="contained" onClick={handleSave} disabled={saving}>
							{saving ? "Saving..." : "Save Settings"}
						</Button>
					</Box>
				</Stack>
			</Paper>
		</Box>
	);
}

export const AdminSettingsPage = pageAuthorizedByGroup(AdminSettingsPageContent, ["Admin"]);